import React, { useEffect, useState } from "react";
import { Button } from "./Button";
import clsx from "clsx";
import seFlagBg from "../assets/images/se-flag-bg.svg";
import seFlagCross from "../assets/images/se-flag-cross.svg";
import gbFlag from "../assets/images/gb-flag.svg";
import deFlag from "../assets/images/de-flag.svg";
import frFlag from "../assets/images/fr-flag.svg";
import { useOfferedLanguages } from "../utils/useOfferedLanguages";

/**
 * The Swedish flag is drawn as two layers so the cross keeps its proportions
 * at this size; the others are single files. Hidden from assistive tech — the
 * language name beside it already says which one this is.
 */
const LanguageFlag = ({ language }: { language: LanguageCode }) => {
  if (language === "sv-SE") {
    return (
      <div className="relative h-6 w-6" aria-hidden>
        <img src={seFlagBg} alt="" className="absolute inset-0" />
        <img src={seFlagCross} alt="" className="absolute inset-0" />
      </div>
    );
  }

  const flags: Record<string, string> = {
    "en-GB": gbFlag,
    "de-DE": deFlag,
    "fr-FR": frFlag,
  };

  return <img src={flags[language]} alt="" aria-hidden className="h-6 w-6" />;
};
import { useTranslation } from "react-i18next";
import { LanguageCode } from "../api/contracts/language-settings";

interface MobileMenuSheetProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLanguage: LanguageCode;
  onLanguageChange: (language: LanguageCode) => void;
}

export const MobileMenuSheet: React.FC<MobileMenuSheetProps> = ({
  isOpen,
  onClose,
  selectedLanguage,
  onLanguageChange,
}) => {
  const { t } = useTranslation();
  const languages = useOfferedLanguages();
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible) return null;
  return (
    <div
      className={clsx(
        "fixed inset-0 z-50 bg-scrim transition-opacity duration-200 ease-out lg:hidden",
        isAnimating ? "opacity-100" : "opacity-0",
      )}
      onClick={onClose}
    >
      <div
        className={clsx(
          "absolute bottom-0 left-0 right-0 flex max-h-[90vh] flex-col overflow-y-auto rounded-t-2xl bg-surface-card transition-transform duration-200 ease-out",
          isAnimating ? "translate-y-0" : "translate-y-full",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrollable Content Container */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 p-4">
            {/* Language Selection */}
            <div className="rounded-2xl border border-border bg-surface-card">
              <div className="p-4">
                <span className="text-base font-bold">
                  {t("language.language")}
                </span>
                <div className="mt-4 space-y-2">
                  {languages.map((language) => (
                    <button
                      key={language}
                      className={clsx(
                        "flex w-full items-center gap-3 rounded-xl p-3 transition-colors duration-200",
                        selectedLanguage === language
                          ? "bg-ink/5"
                          : "hover:bg-ink/5",
                      )}
                      onClick={() => onLanguageChange(language)}
                    >
                      <LanguageFlag language={language} />
                      <span className="text-sm font-medium">
                        {t(`language.${language}`)}
                      </span>
                      {selectedLanguage === language && (
                        <div className="ml-auto h-2 w-2 rounded-circle bg-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Buttons */}
        <div className="border-t border-border bg-surface-card p-4">
          <Button
            variant="secondary"
            size="large"
            onClick={onClose}
            className="w-full"
          >
            {t("common.close")}
          </Button>
        </div>
      </div>
    </div>
  );
};
